import React, { useState, useEffect, useRef } from 'react';
import { Button, Input, Space, Table } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import '../clientPages/styles.css';
import { highlightSearchText } from './usefulFunctions';

const TableComponent = ({
  data,
  columns,
  scrollX,
  // table body height: only the rows scroll, the header stays fixed (sticky)
  scrollY = 'calc(100vh - 290px)',
  loading,
  totalPages,
  onFilters,
  size = 'large',
  disableColumnSearch = false,
  searchField = '',
  searchValue = '',
}) => {
  const [searchText, setSearchText] = useState('');
  const [filters, setFilters] = useState({});
  const searchInput = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeFilter, setActiveFilter] = useState(null);
  const [sorting, setSorting] = useState({ field: '', order: '' });
  const appliedSorting = useRef({ field: '', order: '' });
  const sortSkipped = useRef(false);
  const [filterFields, setFilterFields] = useState({
    page: 1,
    filterField: '',
    sorting: { field: '', order: '' },
    filterValue: '',
  });

  // true when at least one row in the table has a value for this column
  const hasFieldData = (field) =>
    Array.isArray(data) &&
    data.some((row) => {
      const v = row?.[field];
      return v !== undefined && v !== null && String(v).trim() !== '';
    });

  useEffect(() => {
    setCurrentPage(1);
    let nextFilterField = '';
    let nextFilterValue = '';
    if (disableColumnSearch) {
      nextFilterField = searchValue ? searchField : '';
      nextFilterValue = searchValue;
    } else {
      const nreArr = Object.entries(filters).find(
        ([key, value]) => value !== undefined
      );
      nextFilterField = nreArr ? nreArr[0] : '';
      nextFilterValue = nreArr ? nreArr[1] : '';
    }

    // Same flow as before (request + table reload). The only difference:
    // if the sorted column has no data, the sort is not sent, so the table
    // content stays the same.
    let sortToSend = sorting;
    if (sorting.field) {
      if (hasFieldData(sorting.field)) {
        sortSkipped.current = false;
      } else {
        sortSkipped.current = true;
        sortToSend = appliedSorting.current;
      }
    } else if (sortSkipped.current) {
      // clearing a sort that was never applied
      sortSkipped.current = false;
      sortToSend = appliedSorting.current;
    }
    appliedSorting.current = sortToSend;

    setFilterFields((prev) => ({
      ...prev,
      sorting: sortToSend,
      filterField: nextFilterField,
      filterValue: nextFilterValue,
    }));
  }, [filters, sorting, disableColumnSearch, searchField, searchValue]);

  useEffect(() => {
    onFilters(filterFields);
  }, [filterFields]);

  const handleSort = (field) => {
    if (sorting.field === field) {
      if (sorting.order === 'asc') {
        setSorting({ field, order: 'desc' });
      } else if (sorting.order === 'desc') {
        setSorting({ field: '', order: '' });
      } else {
        setSorting({ field, order: 'asc' });
      }
    } else {
      setSorting({ field, order: 'asc' });
    }
  };

  const handleSearch = (key, selectedKey, confirm) => {
    setActiveFilter(key);
    confirm();
    setFilters((prevFilters) => {
      const updatedFilters = Object.fromEntries(
        Object.keys(prevFilters).map((k) => [k, undefined])
      );

      updatedFilters[key] = selectedKey;

      return updatedFilters;
    });
  };

  const handleReset = (key, clearFilters, confirm, close) => {
    setActiveFilter(null);
    clearFilters();
    setFilters(() => ({
      [key]: '',
    }));
    setSearchText('');
    confirm();
    close();
  };

  const getColumnSearchProps = (dataIndex, title) => {
    // if (dataIndex === 'updatedAt') {
    //   return {};
    // }

    return {
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
        close,
      }) => (
        <div style={{ padding: 8 }}>
          <Input
            ref={searchInput}
            placeholder={`Search ${title}`}
            value={selectedKeys[0]}
            onChange={(e) => {
              setSearchText(e.target.value);
              setSelectedKeys(e.target.value ? [e.target.value] : []);
            }}
            onPressEnter={() =>
              handleSearch(dataIndex, selectedKeys[0], confirm)
            }
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => handleSearch(dataIndex, selectedKeys[0], confirm)}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 80 }}
            >
              Search
            </Button>
            <Button
              onClick={() =>
                clearFilters &&
                handleReset(dataIndex, clearFilters, confirm, close)
              }
              size="small"
              style={{ width: 60 }}
            >
              Reset
            </Button>
            <Button
              // type="link"
              size="small"
              style={{ width: 60 }}
              onClick={() => {
                close();
              }}
            >
              Close
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => (
        <SearchOutlined
          style={{
            color: filtered ? '#1890ff' : 'grey',
          }}
        />
      ),
      onFilter: (value, record) => true,
      onFilterDropdownOpenChange: (visible) => {
        if (visible) {
          setTimeout(() => searchInput.current?.select(), 100);
        }
      },
      render: (text) =>
        searchText && dataIndex === activeFilter ? (
          <span>
            {text
              ? text
                  .toString()
                  .split(new RegExp(`(${searchText})`, 'gi'))
                  .map((part, i) =>
                    part.toLowerCase() === searchText.toLowerCase() ? (
                      <span key={i} style={{ backgroundColor: '#ffc069' }}>
                        {part}
                      </span>
                    ) : (
                      part
                    )
                  )
              : null}
          </span>
        ) : (
          text
        ),
    };
  };

  const newColumns = [];

  for (let i = 0; i < columns.length; i++) {
    let obj = {};

    if (
      columns[i].key === 'action' ||
      columns[i].key === 'cStatus' ||
      columns[i]?.change
    ) {
      obj = { ...columns[i] };
    } else {
      obj = {
        ...columns[i],
        sortDirections: ['ascend', 'descend'],
        ...(disableColumnSearch
          ? searchValue
            ? { render: (text) => highlightSearchText(text, searchValue) }
            : {}
          : getColumnSearchProps(`${columns[i].dataIndex}`, columns[i].title)),
        sorter: (a, b) => 0,
        sortOrder:
          sorting.field === `${columns[i].dataIndex}`
            ? sorting.order + 'end'
            : null,
        onHeaderCell: () => ({
          onClick: () => handleSort(`${columns[i].dataIndex}`),
        }),
      };
    }

    newColumns.push(obj);
  }

  return (
    <Table
      size={size}
      loading={loading}
      columns={newColumns}
      dataSource={data}
      pagination={{
        defaultCurrent: currentPage,
        pageSize: 10,
        total: totalPages,
        onChange: (page) => {
          setFilterFields((prev) => ({ ...prev, page: page }));
        },
        showLessItems: true,
        showSizeChanger: false,
      }}
      scroll={{
        x: scrollX,
        y: scrollY,
      }}
    />
  );
};

export default TableComponent;
